// 多画面预览（手机模式）—— 复刻 Android 「门店卡片右上 ⊞ icon → 4 宫格实时画面」
// 路由：/m/multi-view/:storeId?storeName=
// 选门店内最多 4 个有 sn 的在线设备同时播放（flv.js 多实例）
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Spin, Typography } from 'antd';
import { ExclamationCircleOutlined, LeftOutlined, CameraOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Hls from 'hls.js';
import { callInternal } from '../api/internal.js';
import { apiUrl, isCapacitor } from '../utils/api-base.js';
import { fetchFastChannels, fastChannelToDevice } from '../api/fast-device.js';
import { deviceLabel } from '../utils/device-label.js';
import { msePlayer, type MsePlayerInstance } from '../utils/mse-player.js';
import { lockLandscape, lockPortrait } from '../utils/native-orientation.js';
import { useRegion } from '../hooks/useRegion.js';

const PRIMARY = '#FF7043';
// 1×1 透明 GIF poster — 抑制 Android WebView 的灰色「无内容」播放占位 UI
const BLANK_VIDEO_POSTER = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

interface Device {
	device_id: number | string;
	sn?: string;
	name?: string;
	status?: number | string;
	type_id?: number | string;
	channel_id?: string;
}

interface WebUrlResponse {
	play_url: string;
}

const MAX_TILES = 4;

function isPlayableDevice(device: Device): boolean {
	return Boolean(device.sn) && String(device.status ?? '') === '1';
}

function hasDeviceSn(device: Device): boolean {
	return Boolean(device.sn);
}

function NativeVideoUnavailable({ label }: { label: string }) {
	const { t } = useTranslation();
	return (
		<div style={{
			position: 'absolute',
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
			display: 'grid',
			placeItems: 'center',
			color: '#fff',
		}}>
			<div style={{ display: 'grid', justifyItems: 'center', gap: 9, transform: 'translateY(-10px)' }}>
				<ExclamationCircleOutlined style={{ fontSize: 24, color: 'rgba(255,255,255,0.9)' }} />
				<div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14 }}>{t('player.video_init_failed')}</div>
			</div>
			<div
				style={{
					position: 'absolute',
					bottom: 8,
					left: 0,
					right: 0,
					textAlign: 'center',
					color: '#fff',
					fontSize: 14,
					fontWeight: 500,
					textShadow: '0 1px 2px rgba(0,0,0,0.6)',
				}}
			>
				{label}
			</div>
		</div>
	);
}

function VideoTile({ device, index, nativeMode }: { device: Device; index: number; nativeMode: boolean }) {
	const { t } = useTranslation();
	const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [playing, setPlaying] = useState(false);
	const online = String(device.status ?? '') === '1';
	const nativeErrorText = t('player.video_init_failed');

	useEffect(() => {
		if (!videoEl || !device.sn) return;
		if (nativeMode && !online) return;
		let player: MsePlayerInstance | null = null;
		let cancelled = false;
		// 模拟进度从 0 到 90，到拿到 play_url 时跳到 95，开始播跳到 100
		let p = 0;
		const tick = setInterval(() => {
			if (cancelled || playing) return;
			p = Math.min(p + 8 + Math.random() * 5, 92);
			setProgress(Math.round(p));
		}, 200);

		// MSE 支持检测（桌面 flv.js / iOS 17.1+ mpegts.js / 都不行降级 HLS）
		const useFlv = msePlayer.isSupported();
		let hls: Hls | null = null;

		const start = async () => {
			try {
				const res = await callInternal<WebUrlResponse>({
					short_name: 'base',
					action: '/play/weburl',
					query: {
						device_id: device.sn!,
						channel_idx: Number(device.channel_id ?? 1),
						rate: 1000,
						play_type: useFlv ? 'hflv' : 'hls',
					},
					av: '1',
				});
				if (cancelled) return;
				if (!res.data?.play_url) {
					setError(nativeMode ? nativeErrorText : `code=${res.code}`);
					return;
				}
				setProgress(95);
				// HTTPS / Capacitor 包壳 + http:// 流 → 走后端 /api/stream 代理
				const needsProxy = res.data.play_url.startsWith('http://')
					&& (window.location.protocol === 'https:' || isCapacitor());
				const finalUrl = needsProxy
					? apiUrl(`/api/stream?url=${encodeURIComponent(res.data.play_url)}`)
					: res.data.play_url;

				videoEl.muted = true;  // iOS autoplay 必需
				if (useFlv) {
					player = msePlayer.createPlayer(
						{ type: 'flv', url: finalUrl, isLive: true, cors: true },
						{ enableWorker: false, enableStashBuffer: false, stashInitialSize: 128 },
					);
					player.attachMediaElement(videoEl);
					player.load();
					const ret = player.play();
					if (ret && typeof (ret as Promise<void>).then === 'function') {
						(ret as Promise<void>).catch(() => {});
					}
				} else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
					// iOS Safari 原生 HLS
					videoEl.src = finalUrl;
					void videoEl.play().catch(() => {});
				} else if (Hls.isSupported()) {
					// 桌面 Chrome/Firefox：hls.js
					hls = new Hls();
					hls.loadSource(finalUrl);
					hls.attachMedia(videoEl);
					hls.on(Hls.Events.MANIFEST_PARSED, () => { void videoEl.play().catch(() => {}); });
				} else {
					setError(nativeMode ? nativeErrorText : 'no MSE & no native HLS');
					return;
				}

				videoEl.addEventListener('playing', () => {
					setProgress(100);
					setPlaying(true);
				}, { once: true });
			} catch (e) {
				if (!cancelled) setError(nativeMode ? nativeErrorText : (e as Error).message);
			}
		};
		void start();

		return () => {
			cancelled = true;
			clearInterval(tick);
			if (player) {
				try {
					player.pause(); player.unload(); player.detachMediaElement(); player.destroy();
				} catch {}
			}
			if (hls) {
				try { hls.destroy(); } catch {}
			}
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoEl, device.sn, nativeMode, online, nativeErrorText]);

	const isOdd = index % 2 === 0;
	return (
		<div
			style={{
				position: 'relative',
				background: '#000',
				borderRight: isOdd ? '1px solid #1a1a1a' : 'none',
				borderBottom: index < 2 ? '1px solid #1a1a1a' : 'none',
				overflow: 'hidden',
			}}
		>
			<video
				ref={setVideoEl}
				autoPlay
				playsInline
				muted
				poster={BLANK_VIDEO_POSTER}
				style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', background: '#000' }}
			/>
			{nativeMode && (!online || error) && (
				<NativeVideoUnavailable label={deviceLabel(device)} />
			)}
			{/* 时间水印 */}
			{(!nativeMode || (online && !error)) && <div
				style={{
					position: 'absolute',
					top: 4,
					left: 6,
					color: 'rgba(255,255,255,0.85)',
					fontSize: 9,
					fontFamily: 'monospace',
					textShadow: '1px 1px 0 rgba(0,0,0,0.4)',
				}}
			>
				{nativeMode ? dayjs().format('MM-DD-YYYY ddd HH:mm:ss [UTC+08:00]') : dayjs().format('YYYY-MM-DD ddd HH:mm:ss')}
			</div>}
			{/* 截图按钮 */}
			{!nativeMode && <div
				style={{
					position: 'absolute',
					top: 6,
					right: 6,
					width: 26,
					height: 26,
					borderRadius: '50%',
					background: 'rgba(255,255,255,0.85)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					cursor: 'pointer',
				}}
				onClick={(e) => {
					e.stopPropagation();
					if (!videoEl?.videoWidth) return;
					const c = document.createElement('canvas');
					c.width = videoEl.videoWidth; c.height = videoEl.videoHeight;
					c.getContext('2d')?.drawImage(videoEl, 0, 0);
					const a = document.createElement('a');
					a.download = `${deviceLabel(device)}-${dayjs().format('YYYYMMDD-HHmmss')}.png`;
					a.href = c.toDataURL('image/png');
					a.click();
				}}
			>
				<CameraOutlined style={{ fontSize: 14, color: '#444' }} />
			</div>}

			{/* 设备名 */}
			{(!nativeMode || (online && !error)) && <div
				style={{
					position: 'absolute',
					bottom: 8,
					left: 0,
					right: 0,
					textAlign: 'center',
					color: '#fff',
					fontSize: 14,
					fontWeight: 500,
					textShadow: '0 1px 2px rgba(0,0,0,0.6)',
				}}
			>
				{deviceLabel(device)}
			</div>}

			{/* 加载圆环 */}
			{!nativeMode && !playing && (
				<div
					style={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: 6,
					}}
				>
					<RingProgress percent={progress} />
					<Typography.Text style={{ color: '#fff', fontSize: 13 }}>
						{error ?? t('common.loading')}
					</Typography.Text>
				</div>
			)}
		</div>
	);
}

function RingProgress({ percent }: { percent: number }) {
	const size = 60; const stroke = 4; const r = (size - stroke) / 2;
	const c = 2 * Math.PI * r;
	const dash = (percent / 100) * c;
	return (
		<svg width={size} height={size} style={{ display: 'block' }}>
			<circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#444" strokeWidth={stroke} />
			<circle
				cx={size / 2} cy={size / 2} r={r}
				fill="none"
				stroke={PRIMARY}
				strokeWidth={stroke}
				strokeDasharray={`${dash} ${c}`}
				strokeLinecap="round"
				transform={`rotate(-90 ${size / 2} ${size / 2})`}
				style={{ transition: 'stroke-dasharray 0.2s linear' }}
			/>
			<text
				x="50%" y="50%"
				textAnchor="middle" dominantBaseline="central"
				fontSize="14" fill="#fff" fontWeight={600}
			>
				{percent}
			</text>
		</svg>
	);
}

export default function MultiView() {
	const { storeId } = useParams<{ storeId: string }>();
	const [params] = useSearchParams();
	const storeName = params.get('storeName') ?? '';
	const navigate = useNavigate();
	const { t } = useTranslation();
	const region = useRegion();
	const isApac = region === 'apac';
	const [devices, setDevices] = useState<Device[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!isApac || !isCapacitor()) return;
		void lockLandscape();
		return () => {
			void lockPortrait();
		};
	}, [isApac]);

	useEffect(() => {
		if (!storeId) return;
		setDevices(null);
		setError(null);
		// 改用 fastDevice/channel_list（与 iOS 一致 — 仅可播放通道）
		fetchFastChannels(storeId)
			.then((chs) => setDevices(chs.map(fastChannelToDevice)))
			.catch((e) => {
				setError(isApac ? null : (e instanceof Error ? e.message : String(e)));
				setDevices([]);
			});
	}, [storeId, isApac]);

	const tiles = useMemo(() => {
		if (!devices) return [] as Device[];
		// 若 URL 有 ids（来自 MultiSelect 选择页），按选中顺序播放；否则取前 4 个有 sn 的在线设备
		const idsParam = params.get('ids');
		if (idsParam) {
			const ids = idsParam.split(',');
			const byId = new Map(devices.map((d) => [String(d.device_id), d]));
			return ids.map((id) => byId.get(id)).filter((d): d is Device => !!d && (isApac ? hasDeviceSn(d) : isPlayableDevice(d))).slice(0, MAX_TILES);
		}
		return devices
			.filter(isApac ? hasDeviceSn : isPlayableDevice)
			.slice(0, MAX_TILES);
	}, [devices, params, isApac]);

	return (
		<div style={{ background: '#000', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
			{/* 浮在画面上的返回 */}
			<Button
				type="text"
				icon={<LeftOutlined style={{ color: isApac ? PRIMARY : '#fff', fontSize: isApac ? 22 : 18 }} />}
				onClick={() => {
					if (isApac) void lockPortrait();
					navigate(-1);
				}}
				style={{
					position: 'absolute', top: 12, left: 12, zIndex: 10,
					background: isApac ? 'transparent' : 'rgba(255,255,255,0.15)', borderRadius: '50%',
					width: 36, height: 36,
				}}
			/>

			{/* 标题栏（小） */}
			{!isApac && <div style={{ position: 'absolute', top: 12, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 14, zIndex: 5 }}>
				<Typography.Text style={{ color: 'rgba(255,255,255,0.9)' }}>{storeName}</Typography.Text>
			</div>}

			{!devices && (
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
					<Spin />
				</div>
			)}

			{error && !isApac && (
				<div style={{ padding: 56 }}>
					<Alert type="warning" showIcon message={error} />
				</div>
			)}

			{devices && !error && tiles.length === 0 && (
				<div style={{ color: '#ccc', textAlign: 'center', paddingTop: 80 }}>
					{t('common.no_data')}
				</div>
			)}

			{tiles.length > 0 && (
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						gridTemplateRows: '1fr 1fr',
						width: '100vw',
						height: '100vh',
						gap: 0,
					}}
				>
					{Array.from({ length: 4 }).map((_, i) => {
						const d = tiles[i];
						if (!d) {
							return (
								<div
									key={i}
									style={{
										background: '#0a0a0a',
										borderRight: i % 2 === 0 ? '1px solid #1a1a1a' : 'none',
										borderBottom: i < 2 ? '1px solid #1a1a1a' : 'none',
									}}
								/>
							);
						}
						return <VideoTile key={String(d.device_id)} device={d} index={i} nativeMode={isApac} />;
					})}
				</div>
			)}
		</div>
	);
}
